/**
 * StackedBarChart - Composition over time
 *
 * Usage:
 *   <StackedBarChart
 *     title="Time by Project"
 *     data={{
 *       bars: [
 *         { date: 'Mon', project_a: 4, project_b: 2, project_c: 1 },
 *         { date: 'Tue', project_a: 3, project_b: 4, project_c: 2 },
 *       ],
 *       stackKeys: ['project_a', 'project_b', 'project_c']
 *     }}
 *   />
 */

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { BlockProps } from './types';
import { getSeriesColor } from './colors';
import './blocks.css';

export interface StackedBarChartData {
  /** Data points with stacked values */
  bars: Array<Record<string, string | number>>;
  /** Keys to stack */
  stackKeys: string[];
  /** X-axis key */
  xKey?: string;
}

export interface StackedBarChartProps extends BlockProps<StackedBarChartData> {
  /** Chart height in pixels */
  height?: number;
  /** Horizontal stacked bars */
  horizontal?: boolean;
  /** Show as 100% stacked */
  normalized?: boolean;
}

export function StackedBarChart({
  data,
  title,
  config,
  height = 250,
  horizontal = false,
  normalized = false,
  className,
}: StackedBarChartProps) {
  const { bars = [], stackKeys = [], xKey = 'date' } = data ?? {};

  if (!bars || bars.length === 0) {
    return (
      <div className={`block-chart ${className || ''}`}>
        {title && <div className="block-chart-title">{title}</div>}
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-faint)' }}>
          No data available
        </div>
      </div>
    );
  }

  // Normalize data if needed
  const chartData = normalized
    ? bars.map(bar => {
        const total = stackKeys.reduce((sum, key) => sum + (Number(bar[key]) || 0), 0);
        const normalized: Record<string, string | number> = { [xKey]: bar[xKey] };
        stackKeys.forEach(key => {
          normalized[key] = total > 0 ? ((Number(bar[key]) || 0) / total) * 100 : 0;
        });
        return normalized;
      })
    : bars;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    const total = payload.reduce((sum: number, p: any) => sum + (p.value || 0), 0);

    return (
      <div style={{
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: '6px',
        padding: '8px 12px',
        boxShadow: 'var(--popover-shadow, 0 2px 4px rgba(0,0,0,0.1))',
      }}>
        <p style={{
          color: 'var(--color-text-subtle)',
          fontWeight: 600,
          margin: '0 0 8px 0',
          fontSize: '13px',
        }}>
          {label} (Total: {normalized ? '100%' : total.toLocaleString()})
        </p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{
            color: entry.color,
            margin: '4px 0',
            fontSize: '14px',
          }}>
            {entry.name}: {normalized ? `${entry.value.toFixed(1)}%` : entry.value.toLocaleString()}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className={`block-chart ${className || ''}`}>
      {title && <div className="block-chart-title">{title}</div>}

      <div className="block-chart-container">
        <ResponsiveContainer width="100%" height={height}>
          <BarChart
            data={chartData}
            layout={horizontal ? 'vertical' : 'horizontal'}
            margin={{ top: 5, right: 20, bottom: 5, left: horizontal ? 60 : 0 }}
          >
            <CartesianGrid
              strokeDasharray="0"
              stroke="var(--color-border)"
              strokeWidth={1}
              horizontal={!horizontal}
              vertical={horizontal}
            />

            {horizontal ? (
              <>
                <XAxis
                  type="number"
                  stroke="var(--color-text-faint)"
                  tick={{ fill: 'var(--color-text-faint)', fontSize: 10 }}
                  axisLine={{ stroke: 'var(--color-border-strong)', strokeWidth: 1 }}
                  tickLine={false}
                  tickFormatter={(value) => normalized ? `${value}%` : value.toLocaleString()}
                />
                <YAxis
                  type="category"
                  dataKey={xKey}
                  stroke="var(--color-text-faint)"
                  tick={{ fill: 'var(--color-text-faint)', fontSize: 11 }}
                  axisLine={{ stroke: 'var(--color-border-strong)', strokeWidth: 1 }}
                  tickLine={false}
                  width={50}
                />
              </>
            ) : (
              <>
                <XAxis
                  dataKey={xKey}
                  stroke="var(--color-text-faint)"
                  tick={{ fill: 'var(--color-text-faint)', fontSize: 11 }}
                  axisLine={{ stroke: 'var(--color-border-strong)', strokeWidth: 1 }}
                  tickLine={false}
                />
                <YAxis
                  stroke="var(--color-text-faint)"
                  tick={{ fill: 'var(--color-text-faint)', fontSize: 10 }}
                  axisLine={{ stroke: 'var(--color-border-strong)', strokeWidth: 1 }}
                  tickLine={false}
                  tickFormatter={(value) => normalized ? `${value}%` : value.toLocaleString()}
                />
              </>
            )}

            <Tooltip content={<CustomTooltip />} />
            <Legend />

            {stackKeys.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                name={key.replace(/_/g, ' ')}
                stackId="stack"
                fill={config?.colors?.[index] || getSeriesColor(index)}
                radius={index === stackKeys.length - 1 ? [4, 4, 0, 0] : undefined}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default StackedBarChart;
